import { InfoItem } from 'components/molecules/InfoItem/InfoItem';
import { Formik, useField } from 'formik';
import {
  AtsResultType,
  AtsSettingsDocument,
  useSetAtsResultPushConfigMutation,
} from 'generated/graphql';
import { useState } from 'react';
import {
  Button,
  EditFormWrapper,
  FieldLabel,
  SelectFormField,
  TextInput,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import { SwitchRow } from './SwitchRow';
import type { TSelectOption } from '@spotted-zebra-uk/ui-components';
import {
  AtsSettings,
  FIELD_HINTS,
  FIELD_KEYS_BY_TYPE,
  fieldLabel,
  findCompletionStageClash,
  hasAssessmentOrder,
  hasCompletionStage,
  ICIMS_TOOL,
  RESULT_TYPES,
  RESULT_TYPE_LABELS,
  ResultPushEntry,
  ResultPushFormValues,
  resultPushSummary,
  StageOption,
  toFormResultPush,
  toResultPushConfigs,
  WORKDAY_FIELD_HINTS,
  WORKDAY_FIELD_KEYS_BY_TYPE,
  WORKDAY_TOOL,
} from './IntegrationsTab.helpers';
import styles from './IntegrationsTab.module.scss';

const dataTestId = 'integrations-results-form';

/**
 * Channel semantics verified against the gateway/monolith consumers
 * (EM-135; misreading them caused EM-134). Three independent channels:
 * order close, Kombo's native result link on the ATS connector, and
 * ATS-field writes via Kombo passthrough.
 */
const CARD_CAPTION =
  'Each product below has independent delivery channels — the assessment ' +
  'order, Kombo’s native result link on the ATS connector, and ATS fields ' +
  'via Kombo passthrough. Turning one channel off never changes what the ' +
  'others send.';

const INTERVIEW_EVALUATION_RESULT_LINK_CAPTION =
  'Adds a link to the evaluation (evaluator, date, recommended decision) on ' +
  'the candidate’s ATS application via Kombo’s native result-link endpoint ' +
  '(ATS connector), when an evaluator submits a completed evaluation. Needs ' +
  'a connected ATS integration.';

const ORDER_CAPTION =
  'Reports the score and result link back to the assessment order in the ' +
  'ATS and closes it when the result completes. Only applies to candidates ' +
  'invited through an ATS assessment order — when off, their orders stay ' +
  'open in the ATS.';

const RESULT_LINK_CAPTION =
  'Attaches a results link with the score to the candidate’s ATS ' +
  'application via Kombo’s native result-link endpoint (ATS connector). ' +
  'Needs a connected ATS integration — does not change what the assessment ' +
  'order receives.';

const WORKDAY_OBJECTS_CAPTION =
  'Also writes the result values below into a Workday custom object on the ' +
  'candidate through Kombo’s passthrough API when the result completes ' +
  '(re-scores overwrite).';

const INTERVIEW_EVALUATION_WORKDAY_OBJECTS_CAPTION =
  'Writes one row to a Workday custom object on the candidate’s job ' +
  'application through Kombo’s passthrough API each time an evaluator ' +
  'submits or skips an evaluation. The custom object must be multi-instance, ' +
  'with a Text field for the evaluation ID set as its Reference ID — ' +
  'Interview Name must not be the Reference ID, as evaluators on the same ' +
  'interview would overwrite each other. With the Evaluation ID alias set, ' +
  'a repeat of the same submission updates its row; without it, every ' +
  'submission appends a new row.';

const RESULT_LINK_CAPTIONS: Partial<Record<AtsResultType, string>> = {
  [AtsResultType.InterviewEvaluation]: INTERVIEW_EVALUATION_RESULT_LINK_CAPTION,
};

const WORKDAY_OBJECTS_CAPTIONS: Partial<Record<AtsResultType, string>> = {
  [AtsResultType.InterviewEvaluation]:
    INTERVIEW_EVALUATION_WORKDAY_OBJECTS_CAPTION,
};

const COMPLETION_STAGE_CAPTION =
  'When the result completes, also moves the candidate’s ATS application ' +
  'into this stage — but only while it is still in the stage mapped to ' +
  'this result type under Stage mapping, and only once the result is on ' +
  'the record, so “Send to ATS fields” must be on for this result type. ' +
  'Leave it unset to leave the application where it is. Independent of ' +
  '“Progression write-back” — a stage already mapped under Stage mapping ' +
  'cannot be used here. Available for iCIMS only, because a stage ' +
  'reference alone does not say where an application lands in other ' +
  'ATSes.';

const ATS_FIELDS_CAPTION =
  'Also writes the result values below into fields in the ATS through ' +
  'Kombo’s passthrough API when the result completes (re-scores ' +
  'overwrite). Only available for ATSes where passthrough writing is ' +
  'enabled.';

/**
 * Text input with an always-visible label. The design system's
 * `FormikTextInputField` floats its label only once the field has a value,
 * which leaves a dense grid of empty inputs unlabelled.
 */
function LabelledFormikTextInput({
  name,
  label,
  hint,
}: {
  name: string;
  label: string;
  hint?: string;
}) {
  const [field, , helpers] = useField<string>(name);
  return (
    <FieldLabel id={name} label={label}>
      {/* Hint rendered as our own span — FieldLabel's bottomText slot is a
          fixed 16px error line and wrapping copy overflows onto the next
          grid row. */}
      <>
        <TextInput
          id={name}
          value={field.value}
          ariaLabel={label}
          onChange={value => helpers.setValue(value)}
          onBlur={() => helpers.setTouched(true)}
        />
        {hint && <span className={styles.fieldIdCaption}>{hint}</span>}
      </>
    </FieldLabel>
  );
}

type ResultsDeliveryCardProps = {
  companyId: number;
  atsIdentifier: string | null | undefined;
  atsSettings: AtsSettings;
  stageOptions: StageOption[];
  stagesLoading: boolean;
};

export function ResultsDeliveryCard(props: ResultsDeliveryCardProps) {
  const [isEditing, setIsEditing] = useState(false);

  return isEditing ? (
    <ResultsDeliveryForm
      {...props}
      onCancelClick={() => setIsEditing(false)}
      onSave={() => setIsEditing(false)}
    />
  ) : (
    <ResultsDeliveryView {...props} onEditClick={() => setIsEditing(true)} />
  );
}

function ResultsDeliveryView({
  atsIdentifier,
  atsSettings,
  stageOptions,
  onEditClick,
}: ResultsDeliveryCardProps & { onEditClick: () => void }) {
  const isIcims = atsIdentifier === ICIMS_TOOL;
  const isWorkday = atsIdentifier === WORKDAY_TOOL;
  const stageNamesByRef = new Map(
    stageOptions.map(option => [option.value, option.label])
  );
  const entriesByType = new Map<AtsResultType, ResultPushEntry>(
    atsSettings.resultPush.map(entry => [entry.resultType, entry])
  );

  return (
    <div className={styles.viewContainer}>
      <div className={styles.header}>
        <h3 className={styles.title}>Results delivery</h3>
        <Button variant="secondary" size="medium" onClick={onEditClick}>
          Edit
        </Button>
      </div>
      <div className={styles.content}>
        {RESULT_TYPES.map(resultType => (
          <InfoItem
            key={resultType}
            label={RESULT_TYPE_LABELS[resultType]}
            value={resultPushSummary(
              resultType,
              entriesByType.get(resultType),
              isIcims,
              isWorkday,
              stageNamesByRef
            )}
          />
        ))}
      </div>
    </div>
  );
}

type ResultsDeliveryFormValues = {
  resultPush: ResultPushFormValues;
};

function ResultsDeliveryForm({
  companyId,
  atsIdentifier,
  atsSettings,
  stageOptions,
  stagesLoading,
  onCancelClick,
  onSave,
}: ResultsDeliveryCardProps & {
  onCancelClick: () => void;
  onSave: () => void;
}) {
  const { handleMsgType } = useNotification();
  const isIcims = atsIdentifier === ICIMS_TOOL;
  const isWorkday = atsIdentifier === WORKDAY_TOOL;

  const [setAtsResultPushConfigMutation] = useSetAtsResultPushConfigMutation();

  const initialResultPush = toFormResultPush(atsSettings);

  const settingsRefetch = {
    refetchQueries: [{ query: AtsSettingsDocument, variables: { companyId } }],
  };

  const handleSave = async (values: ResultsDeliveryFormValues) => {
    const clash = findCompletionStageClash(
      values.resultPush,
      atsSettings.stageMap
    );
    if (clash) {
      handleMsgType({
        type: TNotification.error,
        title: 'Results delivery settings could not be updated',
        message: clash,
        dismissable: true,
      });
      return;
    }

    const resultPushChanged =
      JSON.stringify(values.resultPush) !== JSON.stringify(initialResultPush);

    try {
      if (resultPushChanged) {
        await setAtsResultPushConfigMutation({
          variables: {
            companyId,
            configs: toResultPushConfigs(values.resultPush, isIcims, isWorkday),
          },
          ...settingsRefetch,
        });
      }
      handleMsgType({
        type: TNotification.success,
        title: 'Results delivery settings updated.',
        dismissable: true,
      });
      onSave();
    } catch {
      handleMsgType({
        type: TNotification.error,
        title: 'Results delivery settings could not be updated',
        message: 'Something went wrong. Please try again.',
        dismissable: true,
      });
    }
  };

  const initialValues: ResultsDeliveryFormValues = {
    resultPush: initialResultPush,
  };

  return (
    <Formik<ResultsDeliveryFormValues>
      initialValues={initialValues}
      onSubmit={handleSave}
    >
      {({ submitForm, values, setFieldValue }) => (
        <EditFormWrapper
          onCancelClick={onCancelClick}
          onSaveClick={submitForm}
          title="Results delivery"
          formId={dataTestId}
          hasResetAllButton={false}
          data-testid={dataTestId}
        >
          <form
            onSubmit={e => e.preventDefault()}
            id={`${dataTestId}-element`}
            className={styles.form}
          >
            <span className={styles.caption}>{CARD_CAPTION}</span>
            {isIcims && !stagesLoading && stageOptions.length === 0 && (
              <span className={styles.fieldIdCaption}>
                No ATS stages are available yet for this company, so a
                completion target cannot be picked — sync jobs first.
              </span>
            )}
            {RESULT_TYPES.map(resultType => {
              const value = values.resultPush[resultType];
              // A saved ref missing from the catalogue still shows, as itself.
              const selectedStage =
                stageOptions.find(
                  option => option.value === value.completionStageRef
                ) ??
                (value.completionStageRef
                  ? {
                      label: value.completionStageRef,
                      value: value.completionStageRef,
                    }
                  : undefined);
              return (
                <div className={styles.resultCard} key={resultType}>
                  <h4 className={styles.resultCardTitle}>
                    {RESULT_TYPE_LABELS[resultType]}
                  </h4>
                  {hasAssessmentOrder(resultType) && (
                    <SwitchRow
                      id={`${resultType}-sendAssessmentOrder`}
                      label="Send result to assessment order"
                      caption={ORDER_CAPTION}
                      value={value.sendAssessmentOrder}
                      switchTestId={`${dataTestId}__${resultType}-order-switch`}
                      onToggle={toggled =>
                        setFieldValue(
                          `resultPush.${resultType}.sendAssessmentOrder`,
                          toggled
                        )
                      }
                    />
                  )}
                  <SwitchRow
                    id={`${resultType}-sendResultLink`}
                    label="Send result link"
                    caption={
                      RESULT_LINK_CAPTIONS[resultType] ?? RESULT_LINK_CAPTION
                    }
                    value={value.sendResultLink}
                    switchTestId={`${dataTestId}__${resultType}-result-link-switch`}
                    onToggle={toggled =>
                      setFieldValue(
                        `resultPush.${resultType}.sendResultLink`,
                        toggled
                      )
                    }
                  />
                  {isIcims && FIELD_KEYS_BY_TYPE[resultType].length > 0 && (
                    <SwitchRow
                      id={`${resultType}-sendToAtsFields`}
                      label="Send to ATS fields (iCIMS)"
                      caption={ATS_FIELDS_CAPTION}
                      value={value.sendToAtsFields}
                      switchTestId={`${dataTestId}__${resultType}-ats-fields-switch`}
                      onToggle={toggled =>
                        setFieldValue(
                          `resultPush.${resultType}.sendToAtsFields`,
                          toggled
                        )
                      }
                    />
                  )}
                  {isIcims &&
                    FIELD_KEYS_BY_TYPE[resultType].length > 0 &&
                    value.sendToAtsFields && (
                      <>
                        <span className={styles.fieldIdCaption}>
                          ATS field integration IDs (blank = field not written)
                        </span>
                        <div className={styles.fieldIdGrid}>
                          {FIELD_KEYS_BY_TYPE[resultType].map(fieldKey => (
                            <LabelledFormikTextInput
                              key={fieldKey}
                              name={`resultPush.${resultType}.fieldIds.${fieldKey}`}
                              label={fieldLabel(resultType, fieldKey)}
                              hint={FIELD_HINTS[fieldKey]}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  {isWorkday && (
                    <SwitchRow
                      id={`${resultType}-sendToWorkdayObjects`}
                      label="Send to Workday custom objects"
                      caption={
                        WORKDAY_OBJECTS_CAPTIONS[resultType] ??
                        WORKDAY_OBJECTS_CAPTION
                      }
                      value={value.sendToWorkdayObjects}
                      switchTestId={`${dataTestId}__${resultType}-workday-objects-switch`}
                      onToggle={toggled =>
                        setFieldValue(
                          `resultPush.${resultType}.sendToWorkdayObjects`,
                          toggled
                        )
                      }
                    />
                  )}
                  {isWorkday && value.sendToWorkdayObjects && (
                    <>
                      <span className={styles.fieldIdCaption}>
                        Workday aliases (blank = not written). Read these from
                        the client&apos;s Workday tenant — aliases are suffixed
                        per tenant and independently at each level, so a field
                        alias may not match its label or the object alias.
                      </span>
                      <LabelledFormikTextInput
                        name={`resultPush.${resultType}.objectAlias`}
                        label="Custom object alias"
                      />
                      <div className={styles.fieldIdGrid}>
                        {WORKDAY_FIELD_KEYS_BY_TYPE[resultType].map(
                          fieldKey => (
                            <LabelledFormikTextInput
                              key={fieldKey}
                              name={`resultPush.${resultType}.fieldAliases.${fieldKey}`}
                              label={fieldLabel(resultType, fieldKey)}
                              hint={WORKDAY_FIELD_HINTS[fieldKey]}
                            />
                          )
                        )}
                      </div>
                    </>
                  )}
                  {isIcims && hasCompletionStage(resultType) && (
                    <>
                      <SelectFormField
                        id={`${dataTestId}__${resultType}-completion-stage`}
                        label="Move to ATS stage on completion"
                        placeholder={
                          stageOptions.length > 0
                            ? 'Move to ATS stage on completion…'
                            : 'No stages available'
                        }
                        isSearchable
                        isDisabled={stageOptions.length === 0}
                        maxMenuHeight={280}
                        options={stageOptions}
                        value={selectedStage}
                        onChange={(option: TSelectOption) =>
                          setFieldValue(
                            `resultPush.${resultType}.completionStageRef`,
                            option?.value ?? ''
                          )
                        }
                      />
                      <span className={styles.fieldIdCaption}>
                        {COMPLETION_STAGE_CAPTION}
                      </span>
                      {value.completionStageRef !== '' && (
                        <div className={styles.reasonListHeader}>
                          <span className={styles.reasonCount}>
                            Moves to {value.completionStageRef}
                          </span>
                          <Button
                            variant="secondary"
                            size="small"
                            onClick={() =>
                              setFieldValue(
                                `resultPush.${resultType}.completionStageRef`,
                                ''
                              )
                            }
                            data-testid={`${dataTestId}__clear-completion-${resultType}`}
                          >
                            Clear
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </form>
        </EditFormWrapper>
      )}
    </Formik>
  );
}
